/**
 * ISLANDS — the map chrome, ported from the aesthetic reference (§12). Owner: S3.
 *
 *   LightIsland  (L2186–2223)  the sun travels an arc as the mood changes; at night
 *                              it sets below the horizon and the moon fades in.
 *                              The reference's four times of day become the app's
 *                              three moods: day, golden, night (labels, clock and arc from TIMES).
 *   ZoomIsland   (L2672–2682)  the .ld-zoom glass column: zoom in, zoom out, recenter
 *                              and the layers toggle, tooltips to the left.
 *   LayersCard   (L2241–2263)  a switch per LayerFlag.
 *
 * Pure presentational: props in, callbacks out. No store access here.
 */
import { useId, type CSSProperties, type JSX, type KeyboardEvent, type ReactNode } from "react";
import type { LayerFlags, Mood } from "@/contracts/store";
import { Icon, type IconName } from "./icons";
import { IconButton, Segmented, Switch } from "./primitives";
import { DUR, EASE_CSS } from "./ease";

const cx = (...a: Array<string | false | null | undefined>): string => a.filter(Boolean).join(" ");

/** CSSProperties plus custom properties (--fy, …). */
type CSSVars = CSSProperties & { [k: `--${string}`]: string | number | undefined };

/* ── LightIsland ────────────────────────────────────────────────────── */

export interface MoodLight {
  label: string;
  clock: string;
  icon: IconName;
  /** Sun arm rotation in degrees: 0 = east horizon (left), 90 = overhead, 180 = west horizon. */
  arc: number;
}

/** The three moods as the light island shows them. Values from the reference's TIMES. */
export const MOOD_LIGHT: Record<Mood, MoodLight> = {
  day: { label: "Day", clock: "12:40 pm", icon: "sun", arc: 90 },
  golden: { label: "Golden hour", clock: "6:05 pm", icon: "sunset", arc: 160 },
  night: { label: "Night", clock: "9:30 pm", icon: "moon", arc: 205 },
};
export const LIGHT_MOODS: readonly Mood[] = ["day", "golden", "night"];

const MOOD_ITEMS = LIGHT_MOODS.map((key) => ({ key, icon: MOOD_LIGHT[key].icon, title: MOOD_LIGHT[key].label }));
const isMood = (k: string): k is Mood => k === "day" || k === "golden" || k === "night";

// The segmented control sits flush inside the island: no second glass layer.
const SEG_STYLE: CSSProperties = { boxShadow: "none", background: "rgba(0,0,0,.14)", backdropFilter: "none", WebkitBackdropFilter: "none" };
const SCENE = `${DUR.scene}ms ${EASE_CSS.drift}`;

export interface LightIslandProps {
  mood: Mood;
  onMood: (m: Mood) => void;
  /** Just the three buttons: no arc, no clock (mobile, kiosk corners). */
  compact?: boolean;
  /** Accessible name of the radio group. Default "Time of day". */
  label?: string;
  className?: string;
  style?: CSSProperties;
}

export function LightIsland({ mood, onMood, compact = false, label = "Time of day", className, style }: LightIslandProps): JSX.Element {
  const t = MOOD_LIGHT[mood] ?? MOOD_LIGHT.golden;
  const night = mood === "night";
  const uid = useId().replace(/[^a-zA-Z0-9_-]/g, "");
  const sky = `ld-sky-${uid}`;
  const glow = `ld-sunglow-${uid}`;
  return (
    <div className={cx("ld-light ld-glass", className)} style={compact ? { paddingLeft: 8, ...style } : style}>
      {!compact && (
        <svg className="ld-light__arc" viewBox="0 0 92 52" aria-hidden="true">
          <defs>
            <clipPath id={sky}>
              <rect x="-10" y="-20" width="112" height="66" />
            </clipPath>
            <radialGradient id={glow}>
              <stop offset="0" style={{ stopColor: "var(--sun)", stopOpacity: 0.8 }} />
              <stop offset="1" style={{ stopColor: "var(--sun)", stopOpacity: 0 }} />
            </radialGradient>
          </defs>
          <path className="arc" d="M8 46A38 38 0 0 1 84 46" />
          <line className="hz" x1="0" y1="46" x2="92" y2="46" />
          <g clipPath={`url(#${sky})`}>
            <g className="ld-light__arm" style={{ transform: `rotate(${t.arc}deg)`, transformOrigin: "46px 46px" }}>
              <circle cx="8" cy="46" r="14" fill={`url(#${glow})`} />
              <circle cx="8" cy="46" r="5.5" style={{ fill: "var(--sun)" }} />
            </g>
          </g>
          <g style={{ opacity: night ? 1 : 0, transition: `opacity ${SCENE}` }}>
            <path d="M31 16.5a6 6 0 1 1-6.6-8 4.8 4.8 0 0 0 6.6 8Z" style={{ fill: "var(--ivory)" }} opacity=".9" />
          </g>
        </svg>
      )}
      <div>
        {!compact && (
          <div className="ld-light__clock ld-num" aria-live="polite">
            <span>{t.label}</span>
            <span style={{ opacity: 0.66 }}>{t.clock}</span>
          </div>
        )}
        <Segmented
          items={MOOD_ITEMS}
          value={mood}
          onChange={(k) => isMood(k) && onMood(k)}
          size="sm"
          label={label}
          className="ld-light__seg"
          style={SEG_STYLE}
        />
      </div>
    </div>
  );
}

/* ── ZoomIsland ─────────────────────────────────────────────────────── */

const SEP_STYLE: CSSProperties = { width: 20, height: 1, margin: "4px 0" };

export interface ZoomIslandProps {
  onZoomIn: () => void;
  onZoomOut: () => void;
  onRecenter: () => void;
  onLayers: () => void;
  layersOpen: boolean;
  recenterLabel?: string;
  /** At the camera's zoom limits. */
  zoomInDisabled?: boolean;
  zoomOutDisabled?: boolean;
  /** id of the layers card, for aria-controls. */
  layersId?: string;
  /** Tooltip side. Default "left" (the island sits on the right edge). */
  tip?: "left" | "right" | "up" | "down";
  className?: string;
  style?: CSSProperties;
}

export function ZoomIsland({
  onZoomIn,
  onZoomOut,
  onRecenter,
  onLayers,
  layersOpen,
  recenterLabel = "Back to Ládera City",
  zoomInDisabled,
  zoomOutDisabled,
  layersId,
  tip = "left",
  className,
  style,
}: ZoomIslandProps): JSX.Element {
  return (
    <div className={cx("ld-zoom ld-glass", className)} style={style} role="group" aria-label="Map view">
      <IconButton icon="plus" label="Zoom in" tip={tip} onClick={onZoomIn} disabled={zoomInDisabled} />
      <IconButton icon="minus" label="Zoom out" tip={tip} onClick={onZoomOut} disabled={zoomOutDisabled} />
      <span className="ld-island__sep" style={SEP_STYLE} aria-hidden="true" />
      <IconButton icon="recenter" label={recenterLabel} tip={tip} onClick={onRecenter} />
      <IconButton icon="layers" label="Layers" tip={tip} pressed={layersOpen} aria-controls={layersId} onClick={onLayers} />
    </div>
  );
}

/* ── LayersCard ─────────────────────────────────────────────────────── */

export interface LayerRow { key: keyof LayerFlags; icon: string; label: string; disabled?: boolean; note?: string }

/** One row per LayerFlag, in the order the card lists them. */
export const LAYER_ROWS: LayerRow[] = [
  { key: "routes", icon: "route", label: "Routes" },
  { key: "future", icon: "ringRoad", label: "Future infrastructure" },
  { key: "contours", icon: "masterplan", label: "Contours" },
  { key: "hillshade", icon: "elevation", label: "Relief" },
  { key: "rings", icon: "target", label: "Distance rings" },
  { key: "boundary", icon: "pin", label: "Site outline" },
  { key: "labels", icon: "list", label: "Place names" },
];

// Rows are <label>s, so the first one is :first-of-type and css/stage.css drops its rule.
// The reference keeps a rule under the header (its rows follow a <div>), so put it back.
const FIRST_ROW: CSSProperties = { borderTop: "1px solid rgba(244,232,218,.08)" };
const ROW_ON: CSSProperties = { cursor: "pointer" };
const ROW_OFF: CSSProperties = { opacity: 0.5, cursor: "default" };
const NOTE_STYLE: CSSProperties = { display: "block", marginTop: 3, font: "400 11.5px/1.3 var(--f-text)", letterSpacing: 0, opacity: 0.6 };

export interface LayersCardProps {
  layers: LayerFlags;
  onLayer: (k: keyof LayerFlags, v: boolean) => void;
  onClose: () => void;
  rows?: LayerRow[];
  /** Heading meta. Default "Layers". */
  title?: string;
  /** For the ZoomIsland's aria-controls. */
  id?: string;
  /** Arrive with .ld-in (default true). */
  enter?: boolean;
  /** Extra content under the rows (a legend, a note). */
  footer?: ReactNode;
  className?: string;
  style?: CSSProperties;
}

export function LayersCard({ layers, onLayer, onClose, rows = LAYER_ROWS, title = "Layers", id, enter = true, footer, className, style }: LayersCardProps): JSX.Element {
  // maxWidth keeps the card on screen on the narrowest phones (zoom column 62 px + 12 px margins).
  const vars: CSSVars = { "--fy": "10px", maxWidth: "calc(100vw - 86px)", ...style };
  const onKey = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key === "Escape") {
      e.stopPropagation();
      onClose();
    }
  };
  return (
    <div id={id} className={cx("ld-layers ld-glass", enter && "ld-in", className)} style={vars} role="dialog" aria-label="Map layers" onKeyDown={onKey}>
      <div className="ld-card__row" style={{ marginBottom: 6 }}>
        <span className="ld-meta">{title}</span>
        <IconButton icon="close" label="Close layers" size={32} iconSize={16} tip={null} onClick={onClose} />
      </div>
      {rows.map((r, i) => (
        // The label names the switch (it has no text of its own), and makes the whole row a target.
        <label key={r.key} className="ld-layers__row" style={{ ...(i === 0 ? FIRST_ROW : null), ...(r.disabled ? ROW_OFF : ROW_ON) }}>
          <span>
            <Icon name={r.icon} size={18} />
            <span style={{ display: "block" }}>
              {r.label}
              {r.note && <small style={NOTE_STYLE}>{r.note}</small>}
            </span>
          </span>
          <Switch checked={!!layers[r.key]} onChange={(v) => onLayer(r.key, v)} disabled={r.disabled} />
        </label>
      ))}
      {footer}
    </div>
  );
}
